// Mirrors @azure/msal-browser types (avoids importing at module level for SSR)
interface MsalConfiguration {
  auth: {
    clientId: string;
    authority: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
  };
  cache: {
    cacheLocation: string;
  };
  system: {
    loggerOptions: {
      logLevel: number;
      piiLoggingEnabled: boolean;
      loggerCallback?: (
        level: number,
        message: string,
        containsPii: boolean
      ) => void;
    };
    allowPlatformBroker: boolean;
  };
}

const LogLevel = {
  Error: 0,
  Warning: 1,
  Info: 2,
  Verbose: 3,
  Trace: 4,
} as const;

const BrowserCacheLocation = {
  LocalStorage: 'localStorage',
  SessionStorage: 'sessionStorage',
  MemoryStorage: 'memoryStorage',
} as const;

function msalLoggerCallback(
  level: number,
  message: string,
  containsPii: boolean
): void {
  if (containsPii) {
    return;
  }

  switch (level) {
    case LogLevel.Error:
      // eslint-disable-next-line no-console
      console.error('[MSAL]', message);
      break;
    case LogLevel.Warning:
      // eslint-disable-next-line no-console
      console.warn('[MSAL]', message);
      break;
    case LogLevel.Info:
      // eslint-disable-next-line no-console
      console.info('[MSAL]', message);
      break;
    case LogLevel.Verbose:
    case LogLevel.Trace:
      // eslint-disable-next-line no-console
      console.debug('[MSAL]', message);
      break;
    default:
      break;
  }
}

export const msalConfig = (
  tenantId: string,
  clientId: string
): MsalConfiguration => ({
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '',
    postLogoutRedirectUri:
      typeof window !== 'undefined' ? window.location.origin : '',
  },
  cache: {
    // sessionStorage required for popup flow (PKCE verifier correlation)
    cacheLocation: BrowserCacheLocation.SessionStorage,
  },
  system: {
    loggerOptions: {
      logLevel:
        process.env.NODE_ENV === 'development'
          ? LogLevel.Verbose
          : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: msalLoggerCallback,
    },
    allowPlatformBroker: false,
  },
});

export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Clears the stale interaction-in-progress flag from sessionStorage.
 * Only removes the status flag, not PKCE/request cache entries.
 */
export function clearStaleInteractionState(clientId: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    const interactionKey = 'msal.interaction.status';
    const value = window.sessionStorage.getItem(interactionKey);

    if (value) {
      try {
        const parsed = JSON.parse(value);
        if (parsed.clientId === clientId) {
          window.sessionStorage.removeItem(interactionKey);

          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(
              '[MSAL] Cleared stale interaction-in-progress flag for clientId:',
              clientId
            );
          }
        }
      } catch {
        window.sessionStorage.removeItem(interactionKey);
      }
    }
  } catch {
    // sessionStorage may be unavailable (e.g. Safari private mode)
  }
}
